import { useEffect, useRef, useState } from 'react'

import Button from '@material-ui/core/Button'
import Grid from '@material-ui/core/Grid'
import { IconButton, MenuItem, Tooltip } from '@material-ui/core'
import { Lock, Unlock } from 'lucide-react'
import { i18nToast } from '../../helpers/i18nToast'
import useAuth from '../../hooks/useAuth.js'
import useSettings from '../../hooks/useSettings'
import { InputFieldText } from '../InputFieldText'
import OnlyForSuperUser from '../OnlyForSuperUser'

import { i18n } from '../../translate/i18n.js'
import { DynamicForm } from '../DynamicForm'
import { s3FormSchema } from './s3options.js'
import { smtpFormSchema } from './smtpoptions.js'
import { useStyles } from './styles.js'
import { useTheme } from '@material-ui/core'

import { generateSecureToken } from '../../helpers/generateSecureToken'
import { copyToClipboard } from '../../helpers/copyToClipboard'
import api from '../../services/api'

import { Files, Settings, Trash2 } from 'lucide-react'
import SelectInput from '../SelectInput/index.js'

const storageTestChecklist = [
  {
    title: 'Testar permissão de escrita',
    text:
      'Faz upload e exclusão de um pequeno arquivo temporário para validar permissão de gravação.'
  },
  {
    title: 'Exibir modo debug em caso de erro',
    text:
      'Abre automaticamente os detalhes técnicos e logs estruturados em caso de falha de conexão.'
  }
]

const storageGuideSteps = [
  'Acesse o console da Backblaze B2.',
  'Vá em Application Keys e crie uma nova chave de API.',
  'Copie o keyID (Access Key) e a applicationKey (Secret Key).',
  'Crie um bucket na aba Buckets e copie o nome e o endpoint S3 correspondente.'
]

const backblazeRegionEndpointMap = {
  'ca-east': 'https://s3.ca-east-006.backblazeb2.com',
  'eu-central': 'https://s3.eu-central-003.backblazeb2.com',
  'us-east': 'https://s3.us-east-005.backblazeb2.com',
  'us-west': 'https://s3.us-west-004.backblazeb2.com'
}

const initialStorageTestResult = {
  title: 'Não Testado',
  message: 'Conexão ainda não testada.',
  checks: [],
  debug: null
}

export default function Service(props) {
  const { settings } = props
  const classes = useStyles()
  const theme = useTheme()

  const [apiToken, setApiToken] = useState('')
  const [isApiTokenDisabled, setIsApiTokenDisabled] = useState(true)
  const [uploadLimit, setUploadLimit] = useState('15')
  const [downloadLimit, setDownloadLimit] = useState('15')
  const [s3ConfigData, setS3ConfigData] = useState({})
  const [smtpConfigData, setSmtpConfigData] = useState({})
  const [storageLocked, setStorageLocked] = useState(true)
  const [smtpLocked, setSmtpLocked] = useState(true)
  const [storageTestState, setStorageTestState] = useState('Não Testado')
  const [storageTestResult, setStorageTestResult] = useState(
    initialStorageTestResult
  )
  const [storageTestLoading, setStorageTestLoading] = useState(false)
  const [storageDebugOpen, setStorageDebugOpen] = useState(false)
  const [smtpTestEmail, setSmtpTestEmail] = useState('')
  const [smtpTestLoading, setSmtpTestLoading] = useState(false)
  const [smtpTestResult, setSmtpTestResult] = useState(null)
  const s3DataFromProps = useRef({})
  const smtpDataFromProps = useRef({})
  const previousS3RegionRef = useRef('')
  const didHydrateS3ConfigRef = useRef(false)

  const limitOptions = Array.from({ length: 10 }, (_, i) => (i + 1) * 10)

  useEffect(() => {
    // If the state is the same as what came from props, it's not a user change, so don't save.
    if (
      JSON.stringify(s3ConfigData) === JSON.stringify(s3DataFromProps.current)
    ) {
      return
    }

    // If we are here, it's a user change that is different from what's in props.
    const timer = setTimeout(() => {
      handleSetting('s3ConfigData', JSON.stringify(s3ConfigData))
    }, 1000)

    return () => {
      clearTimeout(timer)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [s3ConfigData])

  useEffect(() => {
    if (!didHydrateS3ConfigRef.current) {
      return
    }

    const currentRegion = s3ConfigData?.region || ''
    if (currentRegion === previousS3RegionRef.current) {
      return
    }

    previousS3RegionRef.current = currentRegion

    if (s3ConfigData?.service !== 'backblaze') {
      return
    }

    const mappedEndpoint = backblazeRegionEndpointMap[currentRegion]
    if (!mappedEndpoint) {
      return
    }

    setS3ConfigData(prev => {
      if (!prev || prev.endpoint === mappedEndpoint) {
        return prev
      }

      return {
        ...prev,
        endpoint: mappedEndpoint
      }
    })
  }, [s3ConfigData?.region, s3ConfigData?.service])

  useEffect(() => {
    // If the state is the same as what came from props, it's not a user change, so don't save.
    if (
      JSON.stringify(smtpConfigData) ===
      JSON.stringify(smtpDataFromProps.current)
    ) {
      return
    }

    // If we are here, it's a user change that is different from what's in props.
    const timer = setTimeout(() => {
      handleSetting('smtpConfigData', JSON.stringify(smtpConfigData))
    }, 1000)

    return () => {
      clearTimeout(timer)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [smtpConfigData])

  const { getCurrentUserInfo } = useAuth()
  const [currentUser, setCurrentUser] = useState({})

  const { update } = useSettings()

  const handleTestStorageConnection = async () => {
    setStorageTestLoading(true)
    setStorageDebugOpen(false)
    setStorageTestState('Testando...')
    setStorageTestResult({
      title: 'Testando...',
      message: 'Validando comunicação com o bucket informado.',
      checks: [],
      debug: null
    })

    try {
      const { data } = await api.post('/companies/storage/test', {
        ...s3ConfigData,
        testWritePermission: true,
        debugOnError: true
      })

      setStorageTestState(data?.title || 'Conexão validada')
      setStorageTestResult({
        title: data?.title || 'Conexão validada',
        message:
          data?.message ||
          'Comunicação com o bucket validada com sucesso.',
        checks: Array.isArray(data?.checks) ? data.checks : [],
        debug: data?.debug || null
      })
      i18nToast.success('settings.success')
    } catch (error) {
      const responseData = error?.response?.data

      setStorageTestState(responseData?.title || 'Falha na validação')
      setStorageTestResult({
        title: responseData?.title || 'Falha na validação',
        message:
          responseData?.message ||
          'Não foi possível validar a configuração de armazenamento.',
        checks: Array.isArray(responseData?.checks) ? responseData.checks : [],
        debug: responseData?.debug || {
          error: {
            message: error?.message || 'Falha desconhecida no teste.'
          }
        }
      })
      setStorageDebugOpen(true)
    } finally {
      setStorageTestLoading(false)
    }
  }

  useEffect(() => {
    getCurrentUserInfo().then(u => {
      setCurrentUser(u)
    })

    if (Array.isArray(settings) && settings.length) {
      const apiToken = settings.find(s => s.key === 'apiToken')
      setApiToken(apiToken?.value || '')

      const uploadLimit = settings.find(s => s.key === 'uploadLimit')
      setUploadLimit(uploadLimit?.value || '')

      const downloadLimit = settings.find(s => s.key === 'downloadLimit')
      setDownloadLimit(downloadLimit?.value || '')

      const s3ConfigData = settings.find(s => s.key === 's3ConfigData')
      try {
        let parsedS3ConfigData = s3ConfigData?.value
          ? JSON.parse(s3ConfigData.value)
          : {}
        if (parsedS3ConfigData.Serviço) {
          if (!parsedS3ConfigData.service) {
            parsedS3ConfigData.service = parsedS3ConfigData.Serviço
          }
          delete parsedS3ConfigData.Serviço
        }
        s3DataFromProps.current = parsedS3ConfigData
        previousS3RegionRef.current = parsedS3ConfigData?.region || ''
        didHydrateS3ConfigRef.current = true
        setS3ConfigData(parsedS3ConfigData)
      } catch (e) {
        previousS3RegionRef.current = ''
        didHydrateS3ConfigRef.current = true
        setS3ConfigData({})
      }

      const smtpConfigData = settings.find(s => s.key === 'smtpConfigData')
      try {
        const parsedSmtpConfigData = smtpConfigData?.value
          ? JSON.parse(smtpConfigData.value)
          : {}
        smtpDataFromProps.current = parsedSmtpConfigData
        setSmtpConfigData(parsedSmtpConfigData)
      } catch (e) {
        setSmtpConfigData({})
      }
    } else {
      didHydrateS3ConfigRef.current = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settings])

  async function handleDownloadLimit(value) {
    setDownloadLimit(value)
    await update({
      key: 'downloadLimit',
      value
    })
    i18nToast.success('settings.success')
  }

  async function handleSetting(key, value, setter = null) {
    if (setter) {
      setter(value)
    }
    await update({
      key,
      value
    })
    i18nToast.success('settings.success')
  }

  async function generateApiToken() {
    const newToken = generateSecureToken(33)
    setApiToken(newToken)
    setIsApiTokenDisabled(true)
    await update({
      key: 'apiToken',
      value: newToken
    })
    i18nToast.success('settings.success')
  }

  async function deleteApiToken() {
    setApiToken('')
    setIsApiTokenDisabled(false)
    await update({
      key: 'apiToken',
      value: ''
    })
    i18nToast.success('settings.success')
  }

  async function copyApiToken() {
    copyToClipboard(apiToken)
    i18nToast.success('settings.copiedToClipboard')
  }

  const handleTestSmtpConfig = async () => {
    setSmtpTestLoading(true)
    setSmtpTestResult(null)

    try {
      const { data } = await api.post('/companies/smtp/test', {
        ...smtpConfigData,
        testEmailAddress: smtpTestEmail
      })

      setSmtpTestResult({
        success: true,
        title: data?.title || 'Conexão validada',
        message: data?.message || 'Configuração SMTP testada com sucesso.',
        checks: Array.isArray(data?.checks) ? data.checks : []
      })
      i18nToast.success('settings.success')
    } catch (error) {
      const responseData = error?.response?.data

      setSmtpTestResult({
        success: false,
        title: responseData?.title || 'Falha no teste',
        message:
          responseData?.message ||
          'Não foi possível testar a configuração SMTP.',
        checks: Array.isArray(responseData?.checks) ? responseData.checks : []
      })
    } finally {
      setSmtpTestLoading(false)
    }
  }

  return (
    <>
      <Grid container>
        <Grid item xs={12} className={classes.boxGridService}>
          <h3 className={classes.groupTitle}>
            {/* {i18n.t('settings.group.api')} */}
            API WeCloud
          </h3>

          <Grid item xs={12} className={classes.boxContentInputMessage}>
            <Grid xs={6} sm={4} md={6} item>
              <InputFieldText
                id="apiToken"
                label="API Token"
                value={apiToken}
                disabled={isApiTokenDisabled}
                InputProps={{
                  endAdornment: (
                    <>
                      {apiToken && (
                        <>
                          <IconButton
                            size="small"
                            className={classes.iconButton}
                            onClick={copyApiToken}
                          >
                            <Files size={20} />
                          </IconButton>
                          {!isApiTokenDisabled && (
                            <IconButton
                              size="small"
                              className={classes.iconButton}
                              onClick={deleteApiToken}
                            >
                              <Trash2 size={20} />
                            </IconButton>
                          )}
                          <IconButton
                            size="small"
                            className={classes.iconButton}
                            onClick={() =>
                              setIsApiTokenDisabled(!isApiTokenDisabled)
                            }
                          >
                            {isApiTokenDisabled ? (
                              <Lock size={20} />
                            ) : (
                              <Unlock size={20} />
                            )}
                          </IconButton>
                        </>
                      )}
                      {!apiToken && (
                        <IconButton
                          size="small"
                          className={classes.iconButton}
                          onClick={generateApiToken}
                        >
                          <Settings size={20} />
                        </IconButton>
                      )}
                    </>
                  )
                }}
              />
            </Grid>
          </Grid>
        </Grid>

        <Grid item xs={12} className={classes.boxGridService}>
          <h3 className={classes.groupTitle}>
            {i18n.t('settings.group.serveradmin')}
          </h3>

          <Grid item xs={12} className={classes.boxContentInput}>
            <OnlyForSuperUser
              user={currentUser}
              yes={() => (
                <>
                  <Grid
                    container
                    spacing={1}
                    className={classes.transferContainer}
                  >
                    {/* Título da Seção */}
                    <Grid item xs={12}>
                      <h3 className={classes.transferTitle}>
                        Limite de transferência de arquivos
                      </h3>
                    </Grid>

                    {/* Input de Upload */}
                    <Grid item xs={3}>
                      <SelectInput
                        id="uploadLimit"
                        label="Upload"
                        name="uploadLimit"
                        value={uploadLimit}
                        onChange={e => {
                          const val = e.target.value
                          setUploadLimit(val)
                          // Em Selects, geralmente salvamos no onChange,
                          // mas se quiser manter o padrão de só salvar ao perder o foco:
                          handleSetting('uploadLimit', val)
                        }}
                      >
                        {limitOptions.map(option => (
                          <MenuItem key={option} value={option}>
                            {option} Mbps{' '}
                            {/* Adicione a unidade se fizer sentido */}
                          </MenuItem>
                        ))}
                      </SelectInput>
                    </Grid>

                    {/* Input de Download */}
                    <Grid item xs={3}>
                      <SelectInput
                        id="downloadLimit"
                        label="Download"
                        name="downloadLimit"
                        value={downloadLimit}
                        onChange={e => {
                          const val = e.target.value
                          setDownloadLimit(val)
                          handleDownloadLimit(val)
                        }}
                      >
                        {limitOptions.map(option => (
                          <MenuItem key={option} value={option}>
                            {option} Mbps
                          </MenuItem>
                        ))}
                      </SelectInput>
                    </Grid>

                    {/* Texto Explicativo à Direita */}
                    <Grid item xs={5}>
                      <p className={classes.transferDescription}>
                        Arquivos enviados maiores que o limite o contato
                        receberá um link para download
                      </p>
                    </Grid>
                  </Grid>
                  {/* <Grid item xs={2}>
              <InputFieldText
                id="gracePeriod"
                label={i18n.t('settings.GracePeriod.title')}
                name="gracePeriod"
                type="number"
                value={gracePeriod}
                onChange={e => {
                  setGracePeriod(e.target.value)
                }}
                onBlur={async _ => {
                  await handleSetting('gracePeriod', gracePeriod)
                }}
              />
            </Grid> */}
                </>
              )}
            />
          </Grid>
        </Grid>

        <Grid item xs={12} className={classes.boxGridService}>
          <div className={classes.storageSectionHeader}>
            <div>
              <h3 className={classes.groupTitle}>Opções de Armazenamento</h3>
              <p className={classes.storageSectionIntro}>
                Configure e gerencie o armazenamento de arquivos do sistema
                utilizando buckets compatíveis com a API S3 da Backblaze B2.
              </p>
            </div>
            <Tooltip
              title={storageLocked ? 'Desbloquear Campos' : 'Bloquear Campos'}
              arrow
            >
              <IconButton
                size="small"
                onClick={() => setStorageLocked(prev => !prev)}
                className={classes.IconLock}
              >
                {storageLocked ? <Lock size={20} /> : <Unlock size={20} />}
              </IconButton>
            </Tooltip>
          </div>

          <div className={classes.storageStatusCard}>
            <div className={classes.storageStatusBadge}>{storageTestState}</div>
            <p className={classes.storageStatusText}>
              {storageTestResult.message}
            </p>

            <div className={classes.storageTestTitle}>Configurações do teste</div>
            <ul className={classes.storageChecklist}>
              {storageTestChecklist.map(item => (
                <li key={item.title} className={classes.storageChecklistItem}>
                  <strong>{item.title}</strong>
                  <div>{item.text}</div>
                </li>
              ))}
            </ul>

            <Button
              variant="outlined"
              color="primary"
              onClick={handleTestStorageConnection}
              disabled={storageTestLoading}
              style={{ marginTop: '14px' }}
            >
              {storageTestLoading ? 'Testando...' : 'Testar Comunicação'}
            </Button>

            {storageTestResult.checks.length > 0 ? (
              <div className={classes.storageTestResults}>
                {storageTestResult.checks.map(check => (
                  <div key={check.key} className={classes.storageCheckItem}>
                    <div className={classes.storageCheckHeader}>
                      <strong>{check.label}</strong>
                      <span
                        className={
                          check.status === 'success'
                            ? classes.storageCheckStatusSuccess
                            : classes.storageCheckStatusError
                        }
                      >
                        {check.status === 'success' ? 'OK' : 'Falha'}
                      </span>
                    </div>
                    <div className={classes.storageCheckMessage}>
                      {check.message}
                    </div>
                  </div>
                ))}
              </div>
            ) : null}

            {storageTestResult.debug ? (
              <details
                className={classes.storageDebugDetails}
                open={storageDebugOpen}
                onToggle={event => setStorageDebugOpen(event.target.open)}
              >
                <summary className={classes.storageDebugSummary}>
                  Detalhes técnicos do teste
                </summary>
                <pre className={classes.storageDebugPre}>
                  {JSON.stringify(storageTestResult.debug, null, 2)}
                </pre>
              </details>
            ) : null}
          </div>

          <div className={classes.storageGuideCard}>
            <div className={classes.storageGuideTitle}>
              Como obter as credenciais?
            </div>
            <ol className={classes.storageGuideList}>
              {storageGuideSteps.map(step => (
                <li key={step}>{step}</li>
              ))}
            </ol>
            <p className={classes.storageStatusText} style={{ marginTop: 12 }}>
              Para integrar com o Backblaze B2 S3-Compatible, consulte o{' '}
              <a
                className={classes.storageGuideLink}
                href="https://www.backblaze.com/b2/cloud-storage.html"
                target="_blank"
                rel="noreferrer"
              >
                console da Backblaze B2
              </a>
              .
            </p>
          </div>

          <div className={classes.storageFormCard}>
            <div className={classes.storageFormHeader}>
              <h4 className={classes.storageFormTitle}>
                Formulário de armazenamento
              </h4>
              <p className={classes.storageFormDescription}>
                Preencha os dados do bucket, endpoint e credenciais abaixo.
              </p>
            </div>
            <OnlyForSuperUser
              user={currentUser}
              yes={() => (
                <Grid item xs={12}>
                  <Grid item style={{ marginTop: 10 }}>
                    <DynamicForm
                      schema={s3FormSchema}
                      data={s3ConfigData}
                      setData={setS3ConfigData}
                      locked={storageLocked}
                    />
                  </Grid>
                </Grid>
              )}
            />
          </div>
        </Grid>

        <Grid item xs={12} className={classes.boxGridService}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <h3 className={classes.groupTitle}>Configuração SMTP</h3>
            <Tooltip
              title={smtpLocked ? 'Desbloquear Campos' : 'Bloquear Campos'}
              arrow
            >
              <IconButton
                size="small"
                onClick={() => setSmtpLocked(prev => !prev)}
                className={classes.IconLock}
              >
                {smtpLocked ? <Lock size={20} /> : <Unlock size={20} />}
              </IconButton>
            </Tooltip>
          </div>
          <OnlyForSuperUser
            user={currentUser}
            yes={() => (
              <Grid item xs={12}>
                <Grid item style={{ marginTop: 10 }}>
                  <DynamicForm
                    schema={smtpFormSchema}
                    data={smtpConfigData}
                    setData={setSmtpConfigData}
                    locked={smtpLocked}
                  />
                </Grid>
                <Grid
                  container
                  spacing={2}
                  alignItems="flex-end"
                  style={{ marginTop: 16 }}
                >
                  <Grid item xs={12} sm={6} md={4}>
                    <InputFieldText
                      id="smtpTestEmail"
                      name="smtpTestEmail"
                      label="E-mail para teste"
                      type="email"
                      value={smtpTestEmail}
                      onChange={e => setSmtpTestEmail(e.target.value)}
                      disabled={smtpTestLoading}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Button
                      variant="outlined"
                      color="primary"
                      onClick={handleTestSmtpConfig}
                      disabled={
                        smtpTestLoading ||
                        !smtpTestEmail ||
                        !smtpConfigData?.host ||
                        !smtpConfigData?.port
                      }
                      fullWidth
                    >
                      {smtpTestLoading ? 'Testando...' : 'Testar Envio'}
                    </Button>
                  </Grid>
                </Grid>

                {smtpTestResult && (
                  <div
                    className={classes.storageStatusCard}
                    style={{ marginTop: 16 }}
                  >
                    <div
                      className={classes.storageStatusBadge}
                      style={{
                        color: smtpTestResult.success
                          ? theme.palette.success?.main || '#1f8f55'
                          : theme.palette.error?.main || '#c62828'
                      }}
                    >
                      {smtpTestResult.title}
                    </div>
                    <p className={classes.storageStatusText}>
                      {smtpTestResult.message}
                    </p>
                    {smtpTestResult.checks.length > 0 ? (
                      <div className={classes.storageTestResults}>
                        {smtpTestResult.checks.map(check => (
                          <div
                            key={check.key}
                            className={classes.storageCheckItem}
                          >
                            <div className={classes.storageCheckHeader}>
                              <strong>{check.label}</strong>
                              <span
                                className={
                                  check.status === 'success'
                                    ? classes.storageCheckStatusSuccess
                                    : classes.storageCheckStatusError
                                }
                              >
                                {check.status === 'success' ? 'OK' : 'Falha'}
                              </span>
                            </div>
                            <div className={classes.storageCheckMessage}>
                              {check.message}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : null}
                  </div>
                )}
              </Grid>
            )}
          />
        </Grid>
      </Grid>

      {/* <Grid container spacing={3}>
        <OnlyForSuperUser
          user={currentUser}
          yes={() => (
            <>
              <Grid item xs={12}>
                <h2 className={classes.groupTitle}>
                  {i18n.t('settings.group.serveradmin')}
                </h2>
              </Grid>
              <Grid xs={12} sm={6} md={4} item>
                <FormControl className={classes.selectContainer}>
                  <InputLabel id="group-type-label">
                    {i18n.t('settings.AllowRegistration.title')}
                  </InputLabel>
                  <Select
                    labelId="allow-signup"
                    value={allowSignup}
                    onChange={async e => {
                      handleAllowSignup(e.target.value)
                    }}
                  >
                    <MenuItem value={'disabled'}>
                      {i18n.t('settings.AllowRegistration.options.disabled')}
                    </MenuItem>
                    <MenuItem value={'enabled'}>
                      {i18n.t('settings.AllowRegistration.options.enabled')}
                    </MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid xs={12} sm={6} md={4} item>
                <FormControl className={classes.selectContainer}>
                  <TextField
                    id="upload-limit-field"
                    label={i18n.t('settings.FileUploadLimit.title')}
                    variant="standard"
                    name="uploadLimit"
                    value={uploadLimit}
                    onChange={e => {
                      setUploadLimit(e.target.value)
                    }}
                    onBlur={async _ => {
                      await handleSetting('uploadLimit', uploadLimit)
                    }}
                  />
                </FormControl>
              </Grid>

              <Grid xs={12} sm={6} md={4} item>
                <FormControl className={classes.selectContainer}>
                  <TextField
                    id="appname-field"
                    label={i18n.t('settings.FileDownloadLimit.title')}
                    variant="standard"
                    name="appName"
                    value={downloadLimit}
                    inputRef={downloadLimitInput}
                    onChange={e => {
                      setDownloadLimit(e.target.value)
                    }}
                    onBlur={async _ => {
                      await handleDownloadLimit(downloadLimit)
                    }}
                  />
                </FormControl>
              </Grid>

              <Grid xs={12} sm={6} md={4} item>
                <FormControl className={classes.selectContainer}>
                  <TextField
                    id="grace-period-field"
                    label={i18n.t('settings.GracePeriod.title')}
                    variant="standard"
                    name="gracePeriod"
                    type="number"
                    value={gracePeriod}
                    onChange={e => {
                      setGracePeriod(e.target.value)
                    }}
                    onBlur={async _ => {
                      await handleSetting('gracePeriod', gracePeriod)
                    }}
                  />
                </FormControl>
              </Grid>
            </>
          )}
        />
      </Grid> */}
    </>
  )
}
